/**
 * 予約商品制御システム（Shopify用）- Cart API + Product API版（本番向け・デバッグ出力なし）
 * ファイル名: presale-controller.js
 * 配置場所: assets/presale-controller.js
 */

class PresaleController {
  constructor() {
    this.isInitialized = false;
    this.checkInterval = null;
    this.lastCartState = null;
    this.productTagsCache = new Map(); // 商品タグのキャッシュ

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.checkCartAndUpdateUI();
    this.startCartMonitoring();
    this.setupShopifyEventListeners();
    this.setupCartActionListeners();
    this.initializePasswordControl();
    this.isInitialized = true;
  }

  async fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      return cartData;
    } catch (_) {
      return null;
    }
  }

  async fetchProductTags(productHandle) {
    if (this.productTagsCache.has(productHandle)) {
      return this.productTagsCache.get(productHandle);
    }

    try {
      const response = await fetch(`/products/${productHandle}.js`);
      const productData = await response.json();
      const tags = productData.tags || [];
      this.productTagsCache.set(productHandle, tags);
      return tags;
    } catch (_) {
      return [];
    }
  }

  getProductType(tags) {
    if (!tags || !Array.isArray(tags)) return 'normal';
    if (tags.includes('受注期間終了')) return 'order_ended';
    if (tags.includes('#予約2')) return 'presale2';
    if (tags.includes('#予約')) return 'presale';
    return 'normal';
  }

  hasPasswordTag(tags) {
    if (!tags || !Array.isArray(tags)) return false;
    const settings = window.presaleControllerSettings;
    if (!settings || !settings.targetTag) return false;
    return tags.includes(settings.targetTag);
  }

  isWithinValidPeriod() {
    const settings = window.presaleControllerSettings;
    if (!settings) return false;
    if (!settings.startDate || !settings.endDate) return true;

    const now = new Date();
    const start = new Date(settings.startDate.replace(' ', 'T') + '+09:00');
    const end = new Date(settings.endDate.replace(' ', 'T') + '+09:00');
    return now >= start && now <= end;
  }

  async analyzeCart(cartData) {
    if (!cartData || !cartData.items) {
      return {
        presaleCount: 0,
        presale2Count: 0,
        normalCount: 0,
        orderEndedCount: 0,
        passwordRequiredCount: 0,
        isMixed: false,
        hasOrderEnded: false,
        passwordRequired: false,
        isWithinPeriod: false,
        isEmpty: true
      };
    }

    let presaleCount = 0;
    let presale2Count = 0;
    let normalCount = 0;
    let orderEndedCount = 0;
    let passwordRequiredCount = 0;

    for (const item of cartData.items) {
      const productHandle = item.handle;
      const tags = await this.fetchProductTags(productHandle);
      const productType = this.getProductType(tags);

      if (this.hasPasswordTag(tags)) {
        passwordRequiredCount++;
      }

      switch (productType) {
        case 'order_ended':
          orderEndedCount++;
          break;
        case 'presale':
          presaleCount++;
          break;
        case 'presale2':
          presale2Count++;
          break;
        default:
          normalCount++;
      }
    }

    const activeTypeCount = [presaleCount > 0, presale2Count > 0, normalCount > 0].filter(Boolean).length;
    const isMixed = activeTypeCount > 1;
    const hasOrderEnded = orderEndedCount > 0;
    const passwordRequired = passwordRequiredCount > 0;

    let isWithinPeriod = false;
    if (typeof this.isWithinValidPeriod === 'function') {
      isWithinPeriod = this.isWithinValidPeriod();
    }

    return {
      presaleCount,
      presale2Count,
      normalCount,
      orderEndedCount,
      passwordRequiredCount,
      isMixed,
      hasOrderEnded,
      passwordRequired,
      isWithinPeriod,
      isEmpty: cartData.items.length === 0
    };
  }

  showLoadingState() {
    const checkoutButton = document.querySelector('#checkout, .cart__checkout-button, [name="checkout"]');
    const presaleErrorMessage = document.querySelector('#presale-error-message');
    const orderEndedErrorMessage = document.querySelector('#order-ended-error-message');

    if (checkoutButton) {
      checkoutButton.setAttribute('disabled', 'disabled');
      checkoutButton.style.opacity = '0.5';
    }

    this.hideErrorMessage(presaleErrorMessage);
    this.hideErrorMessage(orderEndedErrorMessage);
    this.showLoadingMessage();
  }

  showLoadingMessage() {
    let loadingMessage = document.querySelector('#cart-loading-message');

    if (!loadingMessage) {
      loadingMessage = document.createElement('div');
      loadingMessage.id = 'cart-loading-message';
      loadingMessage.style.cssText = `
        color: #666;
        margin-bottom: 15px;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      `;
      loadingMessage.innerHTML = `
        <div style="
          border: 2px solid #f3f3f3;
          border-top: 2px solid #666;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          animation: spin 1s linear infinite;
        "></div>
        カート内容を確認中...
      `;

      if (!document.querySelector('#loading-spinner-css')) {
        const style = document.createElement('style');
        style.id = 'loading-spinner-css';
        style.textContent = `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `;
        document.head.appendChild(style);
      }

      const errorContainer = document.querySelector('#presale-error-message') ||
                             document.querySelector('#order-ended-error-message');
      if (errorContainer && errorContainer.parentNode) {
        errorContainer.parentNode.insertBefore(loadingMessage, errorContainer);
      }
    }

    loadingMessage.style.display = 'flex';
  }

  async updateProductStatusLabels(cartData, analysis) {
    if (!cartData || !cartData.items) return;

    for (const item of cartData.items) {
      const productHandle = item.handle;
      const tags = await this.fetchProductTags(productHandle);
      const productType = this.getProductType(tags);

      let targetContainer = null;

      const productLinks = document.querySelectorAll(`a[href*="${productHandle}"]`);
      for (const link of productLinks) {
        let parent = link.parentElement;
        for (let i = 0; i < 5 && parent; i++) {
          if (parent.classList.contains('cart-item__details')) {
            targetContainer = parent;
            break;
          }
          parent = parent.parentElement;
        }
        if (targetContainer) break;
      }

      if (!targetContainer) {
        const allContainers = document.querySelectorAll('.cart-item__details');
        for (const container of allContainers) {
          const titleLink = container.querySelector('a');
          if (titleLink && titleLink.textContent.trim() === item.product_title.trim()) {
            targetContainer = container;
            break;
          }
        }
      }

      if (targetContainer) {
        this.addOrUpdateStatusLabel(targetContainer, productType, item.product_title, analysis);
      }
    }
  }

  initializePasswordControl() {
    const passwordInput = document.getElementById('cart-password');
    const passwordSubmit = document.getElementById('password-submit');

    if (!passwordInput || !passwordSubmit) return;

    passwordSubmit.addEventListener('click', () => {
      this.handlePasswordSubmit();
    });

    passwordInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.handlePasswordSubmit();
      }
    });
  }

  handlePasswordSubmit() {
    const passwordInput = document.getElementById('cart-password');
    const passwordError = document.getElementById('password-error');
    const settings = window.presaleControllerSettings;

    if (!passwordInput || !passwordError || !settings) return;

    const inputPassword = passwordInput.value;

    if (inputPassword === settings.password) {
      this.hidePasswordError();
      this.hidePasswordSection();
      this.showCheckoutButton();
    } else {
      this.showPasswordError();
      passwordInput.value = '';
      passwordInput.focus();
    }
  }

  showPasswordSection() {
    const passwordSection = document.getElementById('password-section');
    if (passwordSection) {
      passwordSection.style.display = 'block';
    }
  }

  hidePasswordSection() {
    const passwordSection = document.getElementById('password-section');
    if (passwordSection) {
      passwordSection.style.display = 'none';
    }
  }

  showPasswordError() {
    const passwordError = document.getElementById('password-error');
    if (passwordError) {
      passwordError.style.display = 'block';
    }
  }

  hidePasswordError() {
    const passwordError = document.getElementById('password-error');
    if (passwordError) {
      passwordError.style.display = 'none';
    }
  }

  showCheckoutButton() {
    const checkoutButton = document.querySelector('#checkout, .cart__checkout-button, [name="checkout"]');
    if (checkoutButton) {
      checkoutButton.style.display = 'block';
      checkoutButton.removeAttribute('disabled');
      checkoutButton.setAttribute('type', 'submit');
      checkoutButton.setAttribute('name', 'checkout');
      checkoutButton.setAttribute('form', 'cart');
    }
  }

  hideCheckoutButton() {
    const checkoutButton = document.querySelector('#checkout, .cart__checkout-button, [name="checkout"]');
    if (checkoutButton) {
      checkoutButton.style.display = 'none';
    }
  }

  addOrUpdateStatusLabel(container, productType, productTitle, analysis) {
    if (!container) return;

    const existingLabel = container.querySelector('.product-status-label');
    if (existingLabel) {
      existingLabel.remove();
    }

    let labelText = '';
    let shouldShow = false;

    switch (productType) {
      case 'order_ended':
        labelText = '受注期間は終了しています';
        shouldShow = true;
        break;
      case 'presale':
      case 'presale2':
        labelText = 'こちらは予約商品です';
        shouldShow = true;
        break;
    }

    if (shouldShow && (analysis.hasOrderEnded || analysis.isMixed)) {
      const statusLabel = document.createElement('div');
      statusLabel.className = 'product-status-label';
      statusLabel.style.cssText = `
        color: red;
        font-size: 12px;
        margin-top: 5px;
        padding: 3px 6px;
        display: inline-block;
      `;
      statusLabel.textContent = labelText;

      const optionElement = container.querySelector('p.product-option');
      if (optionElement) {
        optionElement.parentNode.insertBefore(statusLabel, optionElement.nextSibling);
      } else {
        container.insertBefore(statusLabel, container.firstChild);
      }
    }
  }

  hideLoadingMessage() {
    const loadingMessage = document.querySelector('#cart-loading-message');
    if (loadingMessage) {
      loadingMessage.style.display = 'none';
    }
  }

  async updateUI(analysis, cartData = null) {
    const checkoutButton = document.querySelector('#checkout, .cart__checkout-button, [name="checkout"]');
    const presaleErrorMessage = document.querySelector('#presale-error-message');
    const orderEndedErrorMessage = document.querySelector('#order-ended-error-message');

    this.hideLoadingMessage();

    if (cartData) {
      await this.updateProductStatusLabels(cartData, analysis);
    }

    if (!checkoutButton) {
      return;
    }

    if (analysis.isEmpty) {
      checkoutButton.setAttribute('disabled', 'disabled');
      this.hideErrorMessage(presaleErrorMessage);
      this.hideErrorMessage(orderEndedErrorMessage);
      this.hidePasswordSection();
      return;
    }

    if (analysis.hasOrderEnded) {
      this.showErrorMessage(orderEndedErrorMessage);
      this.hideErrorMessage(presaleErrorMessage);
      this.hidePasswordSection();
      this.disableCheckoutButton(checkoutButton);
      return;
    }

    if (analysis.isMixed) {
      this.showErrorMessage(presaleErrorMessage);
      this.hideErrorMessage(orderEndedErrorMessage);
      this.hidePasswordSection();
      this.disableCheckoutButton(checkoutButton);
      return;
    }

    if (analysis.passwordRequired && analysis.isWithinPeriod) {
      this.hideErrorMessage(presaleErrorMessage);
      this.hideErrorMessage(orderEndedErrorMessage);
      this.showPasswordSection();
      this.hideCheckoutButton();
      return;
    }

    this.hideErrorMessage(presaleErrorMessage);
    this.hideErrorMessage(orderEndedErrorMessage);
    this.hidePasswordSection();
    this.enableCheckoutButton(checkoutButton);
  }

  disableCheckoutButton(button) {
    if (!button) return;
    button.setAttribute('disabled', 'disabled');
    button.style.opacity = '0.5';
    button.setAttribute('type', 'button');
    button.removeAttribute('name');
    button.style.display = 'block';
  }

  enableCheckoutButton(button) {
    if (!button) return;
    button.removeAttribute('disabled');
    button.style.opacity = '1';
    button.setAttribute('type', 'submit');
    button.setAttribute('name', 'checkout');
    button.setAttribute('form', 'cart');
    button.style.display = 'block';
  }

  showErrorMessage(container) {
    if (container) {
      container.style.display = 'block';
    }
  }

  hideErrorMessage(container) {
    if (container) {
      container.style.display = 'none';
    }
  }

  async checkCartAndUpdateUI() {
    this.showLoadingState();

    const cartData = await this.fetchCartData();
    if (cartData) {
      const analysis = await this.analyzeCart(cartData);
      await this.updateUI(analysis, cartData);

      this.lastCartState = JSON.stringify(
        cartData.items.map(item => ({
          id: item.id,
          quantity: item.quantity,
          handle: item.handle
        }))
      );
    } else {
      this.hideLoadingMessage();
    }
  }

  startCartMonitoring() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = setInterval(async () => {
      const cartData = await this.fetchCartData();
      if (cartData) {
        const currentCartState = JSON.stringify(
          cartData.items.map(item => ({
            id: item.id,
            quantity: item.quantity,
            handle: item.handle
          }))
        );

        if (this.lastCartState !== currentCartState) {
          this.showLoadingState();
          const analysis = await this.analyzeCart(cartData);
          await this.updateUI(analysis, cartData);
          this.lastCartState = currentCartState;
        }
      }
    }, 3000);
  }

  setupShopifyEventListeners() {
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this.checkCartAndUpdateUI(), 1000);
    });

    document.addEventListener('cart:changed', () => {
      setTimeout(() => this.checkCartAndUpdateUI(), 1000);
    });

    document.addEventListener('ajaxComplete', () => {
      setTimeout(() => this.checkCartAndUpdateUI(), 1000);
    });
  }

  setupCartActionListeners() {
    document.addEventListener('click', (event) => {
      if (event.target.matches('[name="add"], .btn-cart, .cart-add, .add-to-cart')) {
        setTimeout(() => this.checkCartAndUpdateUI(), 1500);
      }
    });

    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name*="updates"], .cart-quantity, .quantity-input')) {
        setTimeout(() => this.checkCartAndUpdateUI(), 1000);
      }
    });

    document.addEventListener('click', (event) => {
      if (event.target.matches('.cart-remove, .remove, [data-cart-remove]')) {
        setTimeout(() => this.checkCartAndUpdateUI(), 1000);
      }
    });
  }

  clearCache() {
    this.productTagsCache.clear();
  }

  destroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.clearCache();
    this.isInitialized = false;
  }
}

window.presaleController = new PresaleController();

window.addEventListener('beforeunload', () => {
  if (window.presaleController) {
    window.presaleController.destroy();
  }
});